'use client';

import type { ProductUnitItem } from '@weberus/shared-types';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { useAtomValue } from 'jotai';
import { EmptyState } from '@yanolja-rnd/nol-design-system';
import DotLoader from '@/app/_components/common/dot-loader';
import { ProductUnitList } from '@/app/_components/feature/product-unit';
import { cn } from '@/app/_shared/utils/tw-cn';
import { useLeisureListSearchListQuery } from '@/app/(pages)/leisure/hooks';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { ResponseError } from '@/app/_shared/utils/fetcher';
import {
  Failover,
  NoCategorySearchResult,
  NoFilterResult,
  NoKeywordSearchResult,
  NotFound,
} from '@/app/_components/common/empty-state';
import { OmBanner } from '@/app/_components/feature/om-banner';
import { RelatedKeyword } from '@/app/_components/feature/related-keyword';

import { useScrollRestoration } from '@/app/_components/feature/plp-srp-filter/use-scroll-restoration';
import { LeisureListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-leisure-web-txtpb';
import { quickFilterAtom, sessionKeyAtom } from '@/app/_shared/store/search-filter';
import { convertToProductUnitProps } from 'app/_components/feature/product-unit/converter';
import { RecommendList } from '@/app/(pages)/local/ui/local-product-list/recommend-list';
import { type GroupedItems } from '@/app/(pages)/local/api/queries';
import { useRecommendedLeisure } from '@/app/(pages)/leisure/ui/product-list/queries';
import { PlaceListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-accommodations-korea-web-txtpb';

const ProductItems = ({
  items,
  listName,
  listServerLogMeta,
}: {
  items: ProductUnitItem[];
  listName: string;
  listServerLogMeta?: Record<string, unknown> | null;
}) => {
  if (!items.length) return null;

  const handleImpression = (item: ProductUnitItem, index: number) => {
    const log = {
      position: index,
      logMeta: { ...item.serverLogMeta, ...(listServerLogMeta || {}) },
      leisureTicketNo: item.data.id,
      listName,
    };
    clientLogProtobuf(LeisureListPageLog.ListNameLeisureTicketNo.impression, log)();
  };

  const handleItemClick = (item: ProductUnitItem, index: number) => {
    const log = {
      position: index,
      logMeta: { ...item.serverLogMeta, ...listServerLogMeta },
      leisureTicketNo: item.data.id,
      listName,
    };
    clientLogProtobuf(LeisureListPageLog.ListNameLeisureTicketNo.click, log)();
    navigateTo(item?.data?.action);
  };

  return (
    <div
      className={cn(
        // 레이아웃
        'flex w-full max-w-legacy-pc-size flex-wrap pc:rounded-12',
        // 보더 + 구분자
        'divide-y divide-line-neutral-weak1 overflow-hidden border-b border-line-neutral-weak1 pc:divide-x pc:border pc:first:!border-t pc:[&>*:nth-child(2)]:!border-t-0 pc:[&>*:nth-child(2n+1)]:!border-l-0',
      )}>
      {items.map((item, index) => (
        <ProductUnitList
          key={item.data.id}
          url={item.data?.action?.web}
          onImpression={() => handleImpression(item, index)}
          onItemClick={() => handleItemClick(item, index)}
          {...convertToProductUnitProps(item)}
        />
      ))}
      {/* 그룹이 홀수일 경우, divide 스타일 적용이 안되므로 임의의 아이템 추가 */}
      {!!(items.length % 2) && <div className="hidden w-full pc:block pc:w-1/2" />}
    </div>
  );
};

interface ProductListProps {
  pageName: string;
  listName: string;
}

export const ProductList = ({ pageName, listName }: ProductListProps) => {
  const quickFilter = useAtomValue(quickFilterAtom);
  const sessionKey = useAtomValue(sessionKeyAtom);
  const {
    filteredList,
    lastItemRef,
    isDataLoading,
    isError,
    hasNextPage,
    error,
    refetch,
    serverLogMeta,
    fetchNextPage,
    isFirstPageLoaded,
  } = useLeisureListSearchListQuery();

  const { header: localLeisureHeader, items: localLeisureItems, isLoading: isLeisureLoading } = useRecommendedLeisure();

  useScrollRestoration(sessionKey); // 스크롤 저장 및 복원 로직 사용

  const handleOmBannerClick = (
    serverLogMeta: Record<string, unknown> | null | undefined,
    index: number,
    action?: { app: string; web: string },
  ) => {
    const log = { position: index, logMeta: { position: index, ...(serverLogMeta || {}) }, listName };
    clientLogProtobuf(LeisureListPageLog.ListNameBannerOm.click, log)();
    action && navigateTo(action);
  };

  const handleOmBannerImpr = (serverLogMeta: Record<string, unknown> | null | undefined, index: number) => {
    const log = { position: index, logMeta: { position: index, ...(serverLogMeta || {}) }, listName };
    clientLogProtobuf(LeisureListPageLog.ListNameBannerOm.impression, log)();
  };

  const isLastProductItemType = Array.isArray(filteredList[filteredList.length - 1]);

  const isVisibleRecommendLeisureList =
    !!localLeisureItems.length && !isDataLoading && !isLeisureLoading && !hasNextPage && localLeisureItems.length > 1;
  // 로그를 위한 position 계산 로직
  const calculateListPosition = (items: GroupedItems[], upToIndex = items.length) => {
    return items
      .slice(0, upToIndex)
      .filter(
        (item) => Array.isArray(item) || (item.type !== 'SECTION_GROUP_HEADER' && item.type !== 'SECTION_DIVIDER'),
      )
      .reduce((acc, cur) => acc + (Array.isArray(cur) ? cur.length : 1), 0);
  };

  const noResult = filteredList.length === 0 && !isDataLoading && isFirstPageLoaded;
  const recommendLeisureListPosition = calculateListPosition(filteredList);

  const renderRecommendLists = () => (
    <>
      {/* 레저 추천 목록 */}
      {isVisibleRecommendLeisureList && (
        <RecommendList
          header={localLeisureHeader?.data}
          listName={listName}
          items={localLeisureItems}
          isNoResult={noResult}
          position={recommendLeisureListPosition}
        />
      )}
    </>
  );

  if (isError && !isFirstPageLoaded) {
    if (error instanceof ResponseError && error) {
      const statusCode = error?.statusCode;
      if (statusCode >= 500) {
        return <Failover refetch={refetch} />;
      }
      if (statusCode === 400 || statusCode === 404) {
        return <NotFound refetch={refetch} />;
      }
    }
    return <NotFound refetch={refetch} />;
  }

  /* 검색 결과 없음 노출 - 필터 결과 없음 | 키워드 검색 결과 없음 | 카테고리 검색 결과 없음 */
  if (!filteredList.length && !isDataLoading) {
    const log = { position: 0, logMeta: { resultCount: 0, ...serverLogMeta }, listName };
    clientLogProtobuf(LeisureListPageLog.ListNameLabelNoAvailableLeisureMsg.impression, log)();

    return (
      <div className="flex w-full max-w-legacy-pc-size flex-col">
        {quickFilter?.hasSelectedFilter ? (
          <NoFilterResult />
        ) : (
          <>
            {pageName === 'CATEGORY' && <NoCategorySearchResult description="카테고리를 변경해 보세요." />}
            {pageName === 'KEYWORD' && <NoKeywordSearchResult />}
            {isVisibleRecommendLeisureList && <div className="border-t border-line-neutral-weak1" />}
            {renderRecommendLists()}
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full max-w-legacy-pc-size flex-col bg-static-white pc:px-16 pc:pt-20 [&>*:first-child]:border-t-0',
        { 'border-b-[1px] border-line-neutral-weak1 pc:border-b-0': !isLastProductItemType },
      )}>
      {filteredList.map((item, index) => {
        if (Array.isArray(item)) {
          return <ProductItems key={index} items={item} listName={listName} listServerLogMeta={serverLogMeta} />;
        }
        if (item.type === 'ONSITE_MARKETING_BANNER') {
          return (
            <OmBanner
              key={index}
              {...item.data}
              onClick={() => handleOmBannerClick(item.serverLogMeta, index, item?.data?.action)}
              onImpression={() => handleOmBannerImpr(item.serverLogMeta, index)}
            />
          );
        }
        if (item.type === 'RELATED_KEYWORD') {
          return (
            <RelatedKeyword
              key={index}
              title={item.data.title}
              items={item.data.items}
              onImpression={() => {
                clientLogProtobuf(PlaceListPageLog.RelatedKeywordsLabelKeyword.impression)();
              }}
              onClick={(keyword: string) => {
                clientLogProtobuf(PlaceListPageLog.RelatedKeywordsLabel.click, {
                  position: index,
                  logMeta: item.serverLogMeta,
                  relatedKeywords: keyword,
                })();
              }}
            />
          );
        }
        return null;
      })}
      <div ref={lastItemRef} />
      {/* 페이지 조회 실패 - 무한 스크롤 페이지 에러 */}
      {isError && (
        <div className="flex h-full w-full justify-center bg-static-white">
          <EmptyState
            title="화면을 불러오지 못했어요"
            pcType="pcLegacySize"
            buttons={[{ content: '다시 시도하기', onClick: () => fetchNextPage() }]}
          />
        </div>
      )}
      {renderRecommendLists()}
      {isDataLoading && (
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
          <DotLoader />
        </div>
      )}
    </div>
  );
};
